import type { CSSProperties } from 'react';

import { CompassStar, type CompassStarRender } from './CompassStar.js';
import { Wordmark } from './Wordmark.js';

interface LogoProps {
  height?: number;
  variant?: 'lockup' | 'mark' | 'wordmark';
  /** Wordmark colour (lockup + wordmark variants). */
  color?: string;
  /** How the compass-star renders (mark + lockup variants). Default: real crystal. */
  markRender?: CompassStarRender;
  /** Colour for the SVG (`gradient`/`mono`) star renders. */
  starColor?: string;
  glow?: boolean;
  style?: CSSProperties | undefined;
}

/**
 * Composite Norvix logo: compass-star + NORVIX wordmark.
 * - `lockup` (default) renders the mark + wordmark side-by-side at the proportions
 *   locked in the Figma brand source (wordmark ≈ 0.28× the mark height).
 * - `mark` renders only the compass-star.
 * - `wordmark` renders only the typographic NORVIX.
 */
export function Logo({
  height = 32,
  variant = 'lockup',
  color = 'var(--text-primary)',
  markRender = 'crystal',
  starColor = 'var(--aurora-500)',
  glow = false,
  style,
}: LogoProps): JSX.Element {
  if (variant === 'mark') {
    return <CompassStar size={height} render={markRender} color={starColor} glow={glow} style={style} />;
  }
  if (variant === 'wordmark') {
    return <Wordmark height={height} color={color} style={style} />;
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: height * 0.1,
        ...style,
      }}
    >
      <CompassStar size={height} render={markRender} color={starColor} glow={glow} />
      <Wordmark height={height * 0.28} color={color} />
    </span>
  );
}
